import { ChevronLeft, ChevronRight } from "lucide-react";
import { CLIENT_PAGE_SIZES, useClientPagination } from "../lib/clientPagination";
import RowActionBar from "./RowActionBar";

/**
 * Reusable row table with toolbar, pagination, and always-visible View actions.
 */
export default function DataRowTable({
  testId = "data-row-table",
  columns = [],
  rows = [],
  rowKey = "id",
  pageSize: defaultPageSize = 10,
  emptyMessage = "No records.",
  hint,
  renderActions,
  showActions = true,
  actionsLabel = "Actions",
}) {
  const { page, setPage, pageSize, setPageSize, pagination } = useClientPagination(rows, {
    defaultPageSize,
    resetKeys: [rowKey],
  });

  const resolveRowId = (row, index) => {
    const raw = row[rowKey] ?? row.id ?? row.code ?? pagination.start + index;
    return String(raw);
  };

  return (
    <div className="data-row-table" data-testid={testId}>
      {hint && <p className="muted small-note admin-crud-hint">{hint}</p>}
      {!rows.length ? (
        <p className="muted admin-crud-empty">{emptyMessage}</p>
      ) : (
        <>
          <div className="admin-crud-toolbar" data-testid={`${testId}-toolbar`}>
            <span className="muted" data-testid={`${testId}-count`}>
              {pagination.total} record{pagination.total === 1 ? "" : "s"}
              {pageSize !== "all" && pagination.total > 0 && (
                <> · showing {pagination.showingFrom}–{pagination.showingTo}</>
              )}
            </span>
            <label className="admin-crud-page-size">
              Rows per page
              <select
                data-testid={`${testId}-page-size`}
                value={pageSize}
                onChange={(e) => {
                  const v = e.target.value;
                  setPageSize(v === "all" ? "all" : Number(v));
                }}
              >
                {CLIENT_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
          </div>
          <div className="admin-crud-table-wrap">
            <table className="admin-crud-table" data-testid={`${testId}-table`}>
              <colgroup>
                {columns.map((col) => (
                  <col key={col.key} style={col.width ? { width: col.width } : undefined} />
                ))}
                {showActions && <col className="admin-crud-actions-col" style={{ width: "280px" }} />}
              </colgroup>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} scope="col">{col.label}</th>
                  ))}
                  {showActions && <th className="admin-crud-actions-col" scope="col">{actionsLabel}</th>}
                </tr>
              </thead>
              <tbody>
                {pagination.pageRows.map((row, rowIndex) => {
                  const stableId = resolveRowId(row, rowIndex);
                  return (
                    <tr key={stableId} className="admin-crud-row" data-testid={`${testId}-row-${stableId}`}>
                      {columns.map((col) => (
                        <td key={col.key} title={col.render ? undefined : String(row[col.key] ?? "")}>
                          {col.render ? col.render(row) : (row[col.key] ?? "—")}
                        </td>
                      ))}
                      {showActions && (
                        <td className="admin-crud-actions-cell">
                          {renderActions ? (
                            renderActions(row, stableId)
                          ) : (
                            <RowActionBar testId={`${testId}-actions`} rowId={stableId} view onView={() => {}} />
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageSize !== "all" && pagination.totalPages >= 1 && (
            <div className="admin-crud-pagination" data-testid={`${testId}-pagination`}>
              <button
                type="button"
                className="secondary-button small-btn"
                disabled={pagination.safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid={`${testId}-page-prev`}
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <span className="admin-crud-page-label" data-testid={`${testId}-page-label`}>
                Page {pagination.safePage} of {pagination.totalPages}
              </span>
              <button
                type="button"
                className="secondary-button small-btn"
                disabled={pagination.safePage >= pagination.totalPages}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                data-testid={`${testId}-page-next`}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
